import React from 'react';

import { Button, Grid } from '@mui/material';

import { NumberField, TextField } from '@commonComponents/Form';
import LocationPicker, { LocationType } from '@commonComponents/GoogleMap/LocationPicker';
import DraggablePositionMap from '@commonComponents/GoogleMap/DraggablePositionMap';

type LocationProps = {
    entity: any;
    onChange: (a: any) => void;
};

const Location: React.FC<LocationProps> = (props) => {
    const { entity, onChange } = props;

    const [showAllFields, setShowAllFields] = React.useState(false);

    const hasError = React.useMemo(() => (
        entity.lat < -90 || entity.lat > 90
        || entity.lng < -180 || entity.lng > 180
        || (entity.lat && !entity.lng)
        || (!entity.lat && entity.lng)
    ), [entity]);

    return (
        <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} lg>
                    <LocationPicker
                        error={hasError}
                        label="Sök adress"
                        defaultText={
                            [entity.street, entity.zip, entity.city, entity.country]
                                .filter(Boolean).join(', ')
                        }
                        onChange={(location: LocationType | null) => {
                            if (location) {
                                onChange({
                                    lat: location.lat,
                                    lng: location.lng,
                                    street: location.street,
                                    zip: location.zip,
                                    city: location.city
                                });
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} lg="auto">
                    <Button
                        variant="text"
                        onClick={() => setShowAllFields(!showAllFields)}
                        sx={{ width: { xs: '100%', lg: 'auto' } }}
                    >
                        {showAllFields ? 'Dölj detaljer' : 'Redigera detaljer'}
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2, display: showAllFields ? undefined : 'none' }}>
                <Grid item xs={12} lg={6}>
                    <NumberField
                        name="lat"
                        label="Latitud"
                        value={entity.lat}
                        onChange={onChange}
                        minValue={-90}
                        maxValue={90}
                        required={entity.lng != null}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <NumberField
                        name="lng"
                        label="Longitud"
                        value={entity.lng}
                        onChange={onChange}
                        minValue={-180}
                        maxValue={180}
                        required={entity.lat != null}
                    />
                </Grid>
                <Grid item xs={0} lg={4} />
                <Grid item xs={12}>
                    <TextField
                        name="street"
                        label="Gata"
                        value={entity.street}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="zip"
                        label="Postnummer"
                        value={entity.zip}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="city"
                        label="Stad"
                        value={entity.city}
                        onChange={onChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DraggablePositionMap
                        lat={entity.lat}
                        lng={entity.lng}
                        onChange={(
                            { lat, lng }
                            : { lat: number, lng: number }
                        ) => (
                            onChange({ lat, lng })
                        )}
                        height="clamp(250px, 45vh, 450px)"
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default Location;
